import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

interface Admin {
  email: string;
  password: string;
}

interface AuthContextType {
  isLoggedIn: boolean;
  adminEmail: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [adminEmail, setAdminEmail] = useState<string | null>(null);

  useEffect(() => {
    // 로컬 스토리지에서 로그인 상태 확인
    const savedEmail = localStorage.getItem('adminEmail');
    if (savedEmail) {
      setIsLoggedIn(true);
      setAdminEmail(savedEmail);
    }
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const adminsRef = collection(db, 'admins');
      const q = query(adminsRef, where('email', '==', email), where('password', '==', password));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        setIsLoggedIn(true);
        setAdminEmail(email);
        localStorage.setItem('adminEmail', email);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const logout = () => {
    setIsLoggedIn(false);
    setAdminEmail(null);
    localStorage.removeItem('adminEmail');
  };

  const value: AuthContextType = {
    isLoggedIn,
    adminEmail,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
